import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import { Vehicle } from '../../types/vehicle';
import vehicleService from '../../services/vehicleService';
import { organizationService } from '../../services/organizationService';
import { FuelReceiptEntry } from '../../components/fuel/FuelReceiptEntry';
import { useAuth } from '../../contexts/AuthContext';

export const QuickFuelReceiptPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const loadVehicles = async () => {
      try {
        if (!user?.organizationId || !user?.id) return;

        // Check if user is Admin
        const adminStatus = await organizationService.isUserAdmin(user.organizationId, user.id);
        setIsAdmin(adminStatus);

        if (adminStatus) {
          // Admin: load all active organization vehicles
          const vehicleList = await vehicleService.getVehiclesByOrganization(user.organizationId, 'active');
          setVehicles(vehicleList);
          if (vehicleList.length > 0) {
            setSelectedVehicle(vehicleList[0]);
          }
        } else {
          // User: load only assigned vehicle
          const assignedVehicle = await vehicleService.getAssignedVehicle(user.organizationId, user.id);

          if (assignedVehicle) {
            setVehicles([assignedVehicle]);
            setSelectedVehicle(assignedVehicle);
          } else {
            setError(t('vehicle.errors.noVehicleAssigned'));
          }
        }
      } catch (err) {
        console.error('Error loading vehicles:', err);
        setError(t('vehicle.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadVehicles();
  }, [user?.organizationId, user?.id, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        p: isMobile ? 2 : 3,
        maxWidth: '100%',
        margin: '0 auto'
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom>
        {t('fuelReceipt.quickEntry.title')}
      </Typography>

      <Paper
        elevation={isMobile ? 0 : 1}
        sx={{
          p: isMobile ? 0 : 2,
          mb: 3
        }}
      >
        {isAdmin ? (
          // Admin: Show vehicle selector
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="vehicle-select-label">
              {t('vehicle.select')}
            </InputLabel>
            <Select
              labelId="vehicle-select-label"
              value={selectedVehicle?.id || ''}
              label={t('vehicle.select')}
              onChange={(e) => {
                const vehicle = vehicles.find(v => v.id === e.target.value);
                setSelectedVehicle(vehicle || null);
              }}
            >
              {vehicles.map((vehicle) => (
                <MenuItem key={vehicle.id} value={vehicle.id}>
                  {vehicle.make} {vehicle.model} - {vehicle.licensePlate}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          // User: Show assigned vehicle info
          selectedVehicle && (
            <Box
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: theme.palette.primary.light,
                borderRadius: 1,
                border: `1px solid ${theme.palette.primary.main}`,
              }}
            >
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {t('vehicle.assignment.assignedVehicle')}
              </Typography>
              <Typography variant="h6">
                {selectedVehicle.make} {selectedVehicle.model}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedVehicle.licensePlate}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('vehicle.mileage.current')}: {selectedVehicle.currentMileage || 0} km
              </Typography>
            </Box>
          )
        )}

        {selectedVehicle && (
          <FuelReceiptEntry
            vehicle={selectedVehicle}
            organizationId={selectedVehicle.organizationId}
            onSuccess={() => {
              // Reset selection for next entry
              if (vehicles.length > 0) {
                setSelectedVehicle(vehicles[0]);
              }
            }}
            onCancel={() => {
              // Reset selection
              if (vehicles.length > 0) {
                setSelectedVehicle(vehicles[0]);
              }
            }}
          />
        )}
      </Paper>
    </Box>
  );
};
